import { useEffect, useState } from "react";
import { CloseGreyIcon } from "../../../assets/icons";
import { ReservationSettingsInfoDTO } from "../../../models/reservation/reservation.response";
import { useNotification } from "../../toster-notification/NotificationContext";
import { useParams } from "react-router-dom";
import { reservationService } from "../../../services/reservationService";
import { handleErrorNotification } from "../../../utils/errorNotificatioMessage";
import Loader from "../../shared/Loader";
import { ReservationStatus } from "../../../utils/mappingEnums";
import { ReservationStatusType } from "../../../models/base/ReservationStatusType";
import { ToLocalDateTimeString, ToLocalTimeString } from "../../../utils/dateFormater";

interface ReservationInfoViewProps {
    onClose: () => void;
    id: string;
}


const ReservationInfoView: React.FC<ReservationInfoViewProps> = ({ onClose, id }) => {

    const [reservationInfo, setReservationInfo] = useState<ReservationSettingsInfoDTO>();
    const [selectedStatus, setSelectedStatus] = useState<ReservationStatusType | undefined>(undefined);

    const [loading, setLoading] = useState(false);

    const { notify } = useNotification();

    const { localId } = useParams<{ localId: string }>();

    const reservationStatusOptions = Object.values(ReservationStatusType)
        .filter(status => typeof status === 'number')
        .map(status => ({
            value: status,
            label: ReservationStatus(status as ReservationStatusType),
        }));


    useEffect(() => {
        fetchReservationInfo();
    }, []);



    const saveChanges = async () => {
        if (!localId) {
            notify("error", "ID lokala nije pronađen.");
            return;
        }

        if (selectedStatus === undefined || selectedStatus === reservationInfo?.status) {
            onClose();
            return;
        }

        setLoading(true);
        try {
            const response = await reservationService.changeStatus(localId, id, selectedStatus);

            if (response) {
                notify("success", "Status rezervacije je uspešno izmenjen.");
                onClose();
            } else {
                notify("error", "Greška pri izmeni statusa rezervacije.");
            }
        } catch (error: any) {
            notify("error", "Greška pri izmeni statusa rezervacije.");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    };


    const fetchReservationInfo = async () => {
        if (!localId) {
            notify("error", "ID lokala nije pronađen.");
            return;
        }

        if (!id) {
            notify("error", "ID rezervacije nije pronađen.");
            return;
        }

        setLoading(true);
        try {
            const response = await reservationService.getReservationInfoById(localId, id);

            if (response.success && response.data) {
                setReservationInfo(response.data);
                selectedStatus === undefined && setSelectedStatus(response.data.status);
            } else {
                notify("error", "Greška pri učitavanju informacija o rezervaciji.");
            }
        } catch (error: any) {
            notify("error", "Greška pri učitavanju informacija o rezervaciji.");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    };

    const reservationStatusClass = (status: ReservationStatusType) => {
        switch (status) {
            case ReservationStatusType.Confirmed:
                return "green-bg";
            case ReservationStatusType.Waiting:
                return "grey-bg";
            case ReservationStatusType.Canceled:
                return "red-bg";
            default:
                return "";
        }
    };

    if (!reservationInfo) {
        return <div>Neuspelo učitavanje informacija o rezervaciji.</div>;
    }

    if (loading) {
        return <Loader />;
    }

    return (
        <section className="modal-section reservation-info">
            <div className="w-layout-blockcontainer container w-container">
                <div className="modal-wrapper">
                    <div className="modal-div">
                        <div className="modal-header-div">
                            <div>
                                <div className="modal-header">Detalji Rezervacije</div>
                                <div className="description-text">
                                    Informacije o rezervaciji #res-1<br />
                                </div>
                            </div>
                            <div className="modal-button-div" onClick={onClose}>
                                <img loading="lazy" src={CloseGreyIcon} alt="" className="icon-size-20 close" />
                            </div>
                        </div>
                        <div className="div-block-38">
                            <div className="div-block-37">
                                <div className="description-text">Trenutni status</div>
                                <div className={`small-box-div ${reservationStatusClass(reservationInfo.status)}`}>
                                    <div>{ReservationStatus(reservationInfo.status)}</div>
                                </div>
                            </div>
                            <select id="field" name="field" data-name="" className="form-select w-select"
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(Number(e.target.value) as ReservationStatusType)}
                            >

                                {reservationStatusOptions.map(option => (
                                    <option key={option.value} value={option.value} selected={option.value === reservationInfo.status}>
                                        {option.label}
                                    </option>
                                ))}

                            </select>
                        </div>

                        <div className="modal-div-body grey-bg reservation">
                            <div className="modal-div-body-header">
                                <div className="modal-div-body-header-text">
                                    Podaci o gostu {reservationInfo?.isForAnotherPerson ? "za koga je rezervacija napravljena" : "koji je napravio rezervaciju"}<br />
                                </div>
                            </div>
                            <div className="div-block-39">
                                <div>
                                    <div className="description-text">Ime i prezime</div>
                                    <div className="_0-95rem">{reservationInfo?.fullName}</div>
                                </div>
                                <div>
                                    <div className="description-text">Telefon</div>
                                    <div className="_0-95rem">{reservationInfo?.phoneNumber}</div>
                                </div>
                            </div>
                            <div className="div-block-39">
                                <div>
                                    <div className="description-text">Email</div>
                                    <div className="_0-95rem">{reservationInfo?.email}</div>
                                </div>
                            </div>
                        </div>

                        {reservationInfo?.isForAnotherPerson && (
                            <div className="modal-div-body grey-bg reservation">
                                <div className="modal-div-body-header">
                                    <div className="modal-div-body-header-text">
                                        Podaci o korisniku koji je kreirao rezervaciju<br />
                                    </div>
                                </div>
                                <div className="div-block-39">
                                    <div>
                                        <div className="description-text">Ime i prezime</div>
                                        <div className="_0-95rem">{reservationInfo?.clientProfile.fullName}</div>
                                    </div>
                                    <div>
                                        <div className="description-text">Telefon</div>
                                        <div className="_0-95rem">{reservationInfo?.clientProfile.phoneNumber}</div>
                                    </div>
                                </div>
                                <div className="div-block-39">
                                    <div>
                                        <div className="description-text">Email</div>
                                        <div className="_0-95rem">{reservationInfo?.clientProfile.email}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="modal-div-body grey-bg reservation">
                            <div className="modal-div-body-header">
                                <div className="modal-div-body-header-text">Detalji rezervacije</div>
                            </div>
                            <div className="div-block-39">
                                <div>
                                    <div className="description-text">Datum i vreme</div>
                                    <div className="_0-95rem">{reservationInfo.reservationDate && ToLocalDateTimeString(reservationInfo.reservationDate)}</div>
                                </div>
                                <div>
                                    <div className="description-text">Do</div>
                                    <div className="_0-95rem">{reservationInfo.departureDate && ToLocalTimeString(reservationInfo.departureDate) || "/"}</div>
                                </div>
                            </div>
                            <div className="div-block-39">
                                <div>
                                    <div className="description-text">Broj osoba</div>
                                    <div className="_0-95rem">{reservationInfo.numberOfPeople}</div>
                                </div>
                                <div>
                                    <div className="description-text">Sto</div>
                                    <div className="_0-95rem">Sto {reservationInfo.tableNumber || "sto nije dodeljen"}</div>
                                </div>
                            </div>
                            <div className="div-block-39">
                                <div id="w-node-_6bf95989-e5ee-865e-5922-8b1187d6e459-1cee91cf">
                                    <div className="description-text">Napomena</div>
                                    <div className="input-div white-bg">
                                        <div className="_0-95rem">{reservationInfo.description || "/"}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="description-text">Kreirana: {reservationInfo.createdAt && ToLocalDateTimeString(reservationInfo.createdAt)}</div>
                            {/* <div className="description-text">Potvrđena: 11.01.2026. 13:46</div> */}
                        </div>
                        <div className="modal-buttons">
                            <div className="button-secondary medium" onClick={onClose}>
                                <div>Otkaži</div>
                            </div>
                            <div className="button-primary medium" onClick={() => saveChanges()}>
                                <div>Sačuvaj izmene</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

    );
};

export default ReservationInfoView;